import axios from "axios";
import { API_BASE_URL } from "configs/AppConfig";
import { AUTH_TOKEN, REFRESH_TOKEN } from "constants/AuthConstant";
import { signOutSuccess, authenticated } from 'store/slices/authSlice';
import store from '../store';
import { forceReconnect as wsReconnect, disconnect as wsDisconnect } from '../wsClient';

// Token helpers
const getStoredToken = () => localStorage.getItem(AUTH_TOKEN);
const getStoredRefreshToken = () => localStorage.getItem(REFRESH_TOKEN);
const setStoredToken = (token) => localStorage.setItem(AUTH_TOKEN, token);
const setStoredRefreshToken = (token) => localStorage.setItem(REFRESH_TOKEN, token);
const clearTokens = () => {
  localStorage.removeItem(AUTH_TOKEN);
  localStorage.removeItem(REFRESH_TOKEN);
};

// Track if we're currently refreshing
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Create axios instance
const apiCall = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
});

/**
 * Request Interceptor
 * Injects Authorization header unless skipAuth = true
 */
apiCall.interceptors.request.use(
  (config) => {
    // Skip auth header if requested
    if (config.skipAuth) {
      return config;
    }

    // SSR-safe check
    const token =
      typeof window !== "undefined"
        ? getStoredToken()
        : null;

    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Response Interceptor with Token Refresh
 */
apiCall.interceptors.response.use(
  (res) => res,
  async (err) => {
    const originalRequest = err.config;
    const status = err?.response?.status;

    // Handle 401 - attempt token refresh
    if (status === 401 && !originalRequest._retry) {
      const isLoginRequest = originalRequest.url?.includes('/auth/login');
      const isRefreshRequest = originalRequest.url?.includes('/auth/refresh-token');
      
      if (isLoginRequest || isRefreshRequest) {
        return Promise.reject(err);
      }

      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers['Authorization'] = `Bearer ${token}`;
          return apiCall(originalRequest);
        }).catch(e => Promise.reject(e));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = getStoredRefreshToken();
      const currentToken = getStoredToken();

      if (!refreshToken) {
        clearTokens();
        wsDisconnect();
        store.dispatch(signOutSuccess());
        return Promise.reject(err);
      }

      try {
        // Decode userId from current token
        let userId = null;
        if (currentToken) {
          try {
            const base64Payload = currentToken.split('.')[1];
            const payload = JSON.parse(atob(base64Payload));
            userId = payload.id;
          } catch (e) {}
        }

        const refreshResponse = await axios.post(`${API_BASE_URL}/auth/refresh-token`, {
          refreshToken,
          userId,
        }, {
          headers: currentToken ? { 'Authorization': `Bearer ${currentToken}` } : {},
          timeout: 10000,
        });

        const { token: newToken, refreshToken: newRefreshToken, user } = refreshResponse.data;

        setStoredToken(newToken);
        if (newRefreshToken) setStoredRefreshToken(newRefreshToken);

        store.dispatch(authenticated({ token: newToken, user }));
        try { wsReconnect(); } catch (e) {}

        processQueue(null, newToken);

        originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
        return apiCall(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearTokens();
        wsDisconnect();
        store.dispatch(signOutSuccess());
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(err);
  }
);

export default apiCall;
